/**
 * EditEntryPage.jsx — v4
 * FIXES:
 *   - BSONError: cash splits no longer send bankId: "" to backend
 *   - Navigation: Cancel/Back/Save all go to ?module=yea&tab=${type}
 *   - Month auto-updates when date is changed
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Plus, Trash2, CheckCircle, XCircle } from 'lucide-react';
import { updateTransaction, getTransactions, getFY, getHeads } from '../../api/yea.api';
import { getSociety } from '../../api/society.api';
import Logo   from '../../components/common/Logo';
import Button from '../../components/common/Button';
import './AddEntryPage.css';

const MONTHS = ['April','May','June','July','August','September','October','November','December','January','February','March'];

const toLocalDateStr = (date) => {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getAllowedDateRange = (fy) => {
  if (!fy) return { min: '', max: '' };
  return { min: toLocalDateStr(fy.startDate), max: toLocalDateStr(fy.endDate) };
};

const monthFromDate = (dateStr) => {
  if (!dateStr) return 'April';
  const d = new Date(dateStr);
  const jsMonth = d.getMonth();
  const fyIndex = jsMonth >= 3 ? jsMonth - 3 : jsMonth + 9;
  return MONTHS[fyIndex];
};

const emptyHead = () => ({ headId: '', headName: '', amount: '' });

const EditEntryPage = () => {
  const { societyId, fyId, type, txnId } = useParams();
  const navigate = useNavigate();
  const label = type === 'income' ? 'Income' : 'Expense';

  const [fy,      setFy]      = useState(null);
  const [society, setSociety] = useState(null);
  const [heads,   setHeads]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [form,    setForm]    = useState(null);
  const [origTxn, setOrigTxn] = useState(null);

  const backUrl = `/dashboard/society/${societyId}/fy/${fyId}?module=yea&tab=${type}`;

  useEffect(() => {
    Promise.all([
      getFY(societyId, fyId),
      getSociety(societyId).catch(() => null),
      getTransactions(societyId, fyId, { type }),
      getHeads(societyId, type),
    ]).then(([fyRes, socRes, txnRes, headRes]) => {
      const loadedFY    = fyRes.data.fy;
      const loadedHeads = headRes.data.heads || [];
      setFy(loadedFY);
      setSociety(socRes?.data?.society || null);
      setHeads(loadedHeads);

      const found = txnRes.data.transactions.find(t => t._id === txnId);
      if (!found) { toast.error('Entry not found'); navigate(backUrl); return; }

      setOrigTxn(found);
      setForm({
        date:       new Date(found.date).toISOString().slice(0, 10),
        month:      found.month || monthFromDate(new Date(found.date).toISOString().slice(0, 10)),
        memberName: found.memberName || '',
        receiptNo:  found.receiptNo  || '',
        narration:  found.narration  || '',
        bankRef:    found.bankRef    || '',
        headEntries: (found.headEntries || []).map(h => ({
          headId:   loadedHeads.find(hd => hd.name === h.headName)?._id || '',
          headName: h.headName,
          amount:   h.amount,
        })),
        paymentSplits: (found.paymentSplits || []).map(s => ({
          mode:     s.mode,
          bankId:   s.bankId   || '',
          bankName: s.bankName || '',
          bankRef:  s.bankRef  || '',
          amount:   s.amount,
        })),
      });
    }).catch(() => { toast.error('Failed to load'); navigate(backUrl); });
  }, [societyId, fyId, type, txnId]);

  const banks = fy?.bankOpeningBalances || [];
  const { min: dateMin, max: dateMax } = getAllowedDateRange(fy);

  const handleDateChange = (val) => {
    setForm(f => ({ ...f, date: val, month: monthFromDate(val) }));
  };

  const addHead    = () => setForm(f => ({ ...f, headEntries: [...f.headEntries, emptyHead()] }));
  const removeHead = i  => setForm(f => ({ ...f, headEntries: f.headEntries.filter((_,idx) => idx !== i) }));
  const setHead    = (i, field, val) => setForm(f => {
    const e = [...f.headEntries];
    e[i] = { ...e[i], [field]: val };
    if (field === 'headId') { const h = heads.find(h => h._id === val); if (h) e[i].headName = h.name; }
    return { ...f, headEntries: e };
  });

  const addSplit    = () => setForm(f => ({ ...f, paymentSplits: [...f.paymentSplits, { mode: 'cash', amount: '' }] }));
  const removeSplit = i  => setForm(f => ({ ...f, paymentSplits: f.paymentSplits.filter((_,idx) => idx !== i) }));
  const setSplit    = (i, field, val) => setForm(f => {
    const s = [...f.paymentSplits];
    s[i] = { ...s[i], [field]: val };
    if (field === 'mode' && val === 'cash') { s[i].bankId = ''; s[i].bankName = ''; s[i].bankRef = ''; }
    if (field === 'mode' && val === 'bank' && banks.length === 1) {
      s[i].bankId = banks[0]._id; s[i].bankName = banks[0].bankName;
    }
    if (field === 'bankId') { const b = banks.find(b => b._id === val); if (b) s[i].bankName = b.bankName; }
    return { ...f, paymentSplits: s };
  });

  const sf = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const headTotal   = form ? form.headEntries.reduce((s, h) => s + (Number(h.amount) || 0), 0) : 0;
  const splitTotal  = form ? form.paymentSplits.reduce((s, p) => s + (Number(p.amount) || 0), 0) : 0;
  const bankMissing = form ? form.paymentSplits.some(s => s.mode === 'bank' && !s.bankId) : false;
  const isValid     = headTotal > 0 && Math.abs(headTotal - splitTotal) < 0.01 && !bankMissing;

  const memberLabel = type === 'income' ? 'Member Name' : 'Particulars';

  const handleSave = async () => {
    if (bankMissing) { toast.error('Please select a bank for all bank payment splits'); return; }
    if (!isValid)    { toast.error('Head totals and payment totals must match'); return; }
    setLoading(true);
    try {
      await updateTransaction(societyId, fyId, txnId, {
        ...form,
        totalAmount:   headTotal,
        headEntries:   form.headEntries.map(h => ({ ...h, amount: Number(h.amount) })),
        // FIX: strip bankId/bankName from cash splits so MongoDB doesn't get "" cast to ObjectId
        paymentSplits: form.paymentSplits.map(s => {
          const split = { mode: s.mode, amount: Number(s.amount) };
          if (s.mode === 'bank' && s.bankId) {
            split.bankId   = s.bankId;
            split.bankName = s.bankName || '';
            split.bankRef  = s.bankRef  || '';
          }
          return split;
        }),
      });
      toast.success('Entry updated');
      navigate(backUrl);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  if (!form || !origTxn) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div className="aep-root">
      <div className="aep-topbar">
        <button className="aep-back" onClick={() => navigate(backUrl)}>
          <ArrowLeft size={14} /> Back
        </button>
        <Logo size="sm" />
        <div className="aep-topbar-title">
          <span className="aep-society">{society?.name || ''}</span>
          <span className="aep-badge">Edit {label} Entry</span>
          {fy && <span className="aep-fy">{fy.fyName}</span>}
        </div>
      </div>

      <div className="aep-body">
        <div className="aep-form-wrap">

          {/* When */}
          <div className="aep-section">
            <div className="aep-section-label">When</div>
            <div className="aep-row">
              <div className="aep-field">
                <label>Month</label>
                <select value={form.month} onChange={sf('month')}>
                  {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div className="aep-field">
                <label>Date</label>
                <input type="date" value={form.date} min={dateMin} max={dateMax}
                  onChange={e => handleDateChange(e.target.value)} />
                {dateMin && <span className="aep-hint">Allowed: {dateMin} to {dateMax}</span>}
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="aep-section">
            <div className="aep-section-label">Details</div>
            <div className="aep-row">
              <div className="aep-field">
                <label>{memberLabel}</label>
                <input type="text" value={form.memberName} onChange={sf('memberName')} />
              </div>
              {type === 'income' ? (
                <div className="aep-field">
                  <label>Receipt No</label>
                  <input type="text" value={form.receiptNo} onChange={sf('receiptNo')} />
                </div>
              ) : (
                <div className="aep-field">
                  <label>Voucher No</label>
                  <div className="aep-readonly">{origTxn.voucherNo || 'Auto-generated'}</div>
                </div>
              )}
              <div className="aep-field" style={{ gridColumn: 'span 2' }}>
                <label>Narration</label>
                <input type="text" placeholder="e.g. Maintenance for Jan 2026…"
                  value={form.narration} onChange={sf('narration')} />
              </div>
            </div>
          </div>

          {/* Head entries */}
          <div className="aep-section">
            <div className="aep-section-label">{label} Heads</div>
            <div className="aep-heads-table">
              <div className="aep-heads-header">
                <span>Account Head</span><span>Amount (₹)</span><span></span>
              </div>
              {form.headEntries.map((h, i) => (
                <div key={i} className="aep-heads-row">
                  <select value={h.headId} onChange={e => setHead(i, 'headId', e.target.value)}>
                    <option value="">{heads.length === 0 ? '⚠ No heads' : 'Select head'}</option>
                    {heads.map(hd => <option key={hd._id} value={hd._id}>{hd.name}</option>)}
                  </select>
                  <input type="number" min="0" step="0.01" placeholder="0.00"
                    value={h.amount} onChange={e => setHead(i, 'amount', e.target.value)}
                    onWheel={e => e.target.blur()} />
                  <button className="aep-remove" onClick={() => removeHead(i)} disabled={form.headEntries.length === 1}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              <button className="aep-add-row" onClick={addHead}><Plus size={13} /> Add another head</button>
            </div>
          </div>

          {/* Payment splits */}
          <div className="aep-section">
            <div className="aep-section-label">Payment Mode</div>
            <div className="aep-heads-table">
              <div className="aep-heads-header">
                <span>Mode</span><span>Bank Name</span><span>Bank Ref (Cheque/UTR/IMPS)</span><span>Amount (₹)</span><span></span>
              </div>
              {form.paymentSplits.map((s, i) => (
                <div key={i} className="aep-split-row" style={{ gridTemplateColumns: '110px 1fr 1fr 120px 36px' }}>
                  <select value={s.mode} onChange={e => setSplit(i, 'mode', e.target.value)}>
                    <option value="cash">Cash</option>
                    <option value="bank">Bank</option>
                  </select>
                  {s.mode === 'bank' ? (
                    <select value={s.bankId || ''} onChange={e => setSplit(i, 'bankId', e.target.value)}
                      style={{ borderColor: !s.bankId ? 'var(--danger)' : undefined }}>
                      <option value="">Select bank *</option>
                      {banks.map(b => <option key={b._id} value={b._id}>{b.bankName}</option>)}
                    </select>
                  ) : <div className="aep-cash-label">Cash payment</div>}
                  {s.mode === 'bank' ? (
                    <input type="text" placeholder="Cheque No / UTR / IMPS ref"
                      value={s.bankRef || ''} onChange={e => setSplit(i, 'bankRef', e.target.value)} />
                  ) : <div />}
                  <input type="number" min="0" step="0.01" placeholder="0.00"
                    value={s.amount} onChange={e => setSplit(i, 'amount', e.target.value)}
                    onWheel={e => e.target.blur()} />
                  <button className="aep-remove" onClick={() => removeSplit(i)} disabled={form.paymentSplits.length === 1}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              <button className="aep-add-row" onClick={addSplit}><Plus size={13} /> Add another payment</button>
            </div>
          </div>

        </div>
      </div>

      {/* Sticky footer */}
      <div className="aep-footer">
        <div className="aep-validation">
          {bankMissing && <span className="aep-invalid"><XCircle size={15}/> Select a bank for all bank payments</span>}
          {!bankMissing && headTotal > 0 && (
            isValid
              ? <span className="aep-valid"><CheckCircle size={15}/> Totals match — ₹{headTotal.toLocaleString('en-IN')}</span>
              : <span className="aep-invalid"><XCircle size={15}/> Heads ₹{headTotal.toLocaleString('en-IN')} · Payments ₹{splitTotal.toLocaleString('en-IN')} — must match</span>
          )}
        </div>
        <div className="aep-actions">
          <Button variant="subtle" onClick={() => navigate(backUrl)}>Cancel</Button>
          <Button loading={loading} disabled={!isValid} onClick={handleSave}>Save Changes</Button>
        </div>
      </div>
    </div>
  );
};

export default EditEntryPage;
